// Homepage fragrance-family wheel: classifies perfumes by keywords in their text, filters them, and renders matching cards in place.
const FAMILY_IDS = ['floral', 'soft_floral', 'floral_oriental', 'soft_oriental', 'oriental', 'woody_oriental', 'woods', 'mossy', 'fougere', 'aromatic', 'citrus', 'fruity'];
const AR_LETTERS = '\\u0621-\\u064A';

// A trailing * lets a term continue (rose* would also match rosemary, so most English terms stay strict).
function termPattern(term) {
  const open = term.endsWith('*');
  const word = open ? term.slice(0, -1) : term;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (/[\u0600-\u06FF]/.test(word)) {
    const tail = open ? '' : `(?![${AR_LETTERS}])`;
    return `(?<![${AR_LETTERS}])(?:وال|بال|لل|ال|و|ب)?${escaped}${tail}`;
  }
  return open ? `\\b${escaped}` : `\\b${escaped}(?:s|es)?\\b`;
}

function matcher(terms) {
  return new RegExp(terms.map(termPattern).join('|'), 'iu');
}

const KEYWORDS = {
  floral: matcher(['rose', 'jasmin*', 'peon*', 'freesia', 'lily', 'lilies', 'orchid', 'tuberose', 'gardenia', 'violet', 'iris', 'magnolia', 'flower*', 'floral*', 'blossom*', 'neroli', 'ylang*', 'heliotrope', 'lotus', 'carnation', 'mimosa', 'osmanthus', 'geranium', 'floralnote', 'ورد*', 'ورود', 'ياسمين*', 'فاوانيا', 'فريزيا', 'زنبق*', 'أوركيد*', 'اوركيد*', 'زهور', 'زهر*', 'بنفسج*', 'سوسن*', 'ماغنوليا', 'جاردينيا', 'نيرولي', 'ليلك']),
  musk: matcher(['musk*', 'مسك*']),
  sweet: matcher(['vanill*', 'caramel*', 'tonka', 'honey', 'praline*', 'gourmand*', 'sugar*', 'chocolate', 'cocoa', 'marshmallow*', 'toffee', 'فانيل*', 'كراميل*', 'تونكا', 'عسل*', 'برالين*', 'سكر*', 'شوكولا*', 'مارشميلو']),
  oriental: matcher(['amber*', 'incense', 'oud', 'agarwood', 'cinnamon', 'cardamom', 'spice*', 'spicy', 'saffron', 'clove*', 'myrrh', 'labdanum', 'oriental*', 'frankincense', 'nutmeg', 'pepper', 'عنبر*', 'بخور*', 'عود', 'قرفة', 'قرفه', 'هيل', 'حبهان', 'توابل*', 'بهارات*', 'زعفران*', 'شرقي*', 'فلفل*', 'قرنفل*', 'لبان']),
  woods: matcher(['sandalwood*', 'cedar*', 'vetiver', 'wood', 'woods', 'woody', 'woodsnote', 'guaiac*', 'cashmeran', 'صندل*', 'أرز', 'ارز', 'نجيل*', 'خشب*', 'أخشاب*', 'اخشاب*']),
  mossy: matcher(['patchouli*', 'moss*', 'oakmoss', 'chypre', 'باتشول*', 'طحلب*', 'شيبر*']),
  lavender: matcher(['lavend*', 'لافندر*', 'خزامى']),
  fougereWord: matcher(['foug*', 'coumarin', 'فوجير*']),
  tonka: matcher(['tonka', 'تونكا']),
  aromatic: matcher(['mint', 'peppermint', 'spearmint', 'sage', 'basil', 'rosemary', 'herb*', 'thyme', 'aromatic*', 'artemisia', 'tarragon', 'juniper', 'نعناع*', 'مريمية', 'ريحان*', 'روزماري', 'إكليل الجبل', 'اكليل الجبل', 'أعشاب*', 'اعشاب*', 'عشبي*', 'زعتر*']),
  citrus: matcher(['bergamot*', 'lemon*', 'orange*', 'mandarin*', 'tangerine*', 'grapefruit*', 'lime', 'citrus*', 'yuzu', 'برغموت*', 'برجموت*', 'ليمون*', 'برتقال*', 'يوسفي*', 'جريب فروت', 'حمضي*', 'حمضيات']),
  fruity: matcher(['berry', 'berries', 'raspberr*', 'blackcurrant*', 'cassis', 'strawberr*', 'peach*', 'plum*', 'pomegranate*', 'pear', 'apple*', 'lychee*', 'litchi', 'fruit*', 'mango*', 'pineapple*', 'cherry', 'cherries', 'apricot*', 'melon*', 'watermelon*', 'fig', 'توت*', 'خوخ*', 'رمان*', 'فراول*', 'كمثرى', 'إجاص', 'اجاص', 'تفاح*', 'ليتشي', 'فاكه*', 'فواكه*', 'مانجو*', 'أناناس', 'اناناس', 'كرز*', 'مشمش*', 'شمام*', 'بطيخ*', 'عنب']),
};

// Exceptions run before matching: rosewood is wood, honeysuckle is neither floral nor sweet, citrus/fruit blossoms are flowers only.
const EXCEPTIONS = [
  [/rose\s?wood|bois de rose|خشب الورد/giu, ' woodsnote '],
  [/honey\s?suckle|زهر(?:ة)? العسل/giu, ' '],
  [/(?:orange|lemon|citrus|cherry|berry|apple|peach|plum)\s(?:blossom|flower)s?|زهر(?:ة)? (?:البرتقال|الليمون|الكرز|التوت)/giu, ' floralnote '],
];

const GENDER = {
  women: /\b(?:women|woman|womens|femme|female|her|ladies|lady|for here)\b|حريمي|نسائي|للنساء|للسيدات/iu,
  men: /\b(?:men|man|mens|homme|male|him|gentleman)\b|رجالي|للرجال/iu,
  unisex: /\bunisex\b|للجنسين/iu,
};
const SEASONS = {
  spring: /\bspring\b|ربيع/iu,
  summer: /\bsummer\b|صيف/iu,
  autumn: /\b(?:autumn|fall season)\b|خريف/iu,
  winter: /\bwinter\b|شتا|شتوي/iu,
};
const OCCASIONS = {
  daily: /\b(?:everyday|every day|daily|daytime|day-to-day)\b|يومي/iu,
  work: /\b(?:office|work|workday|business)\b|شغل|العمل|المكتب/iu,
  evening: /\b(?:evening|night|nighttime|date night)\b|سهر|مسائي|ليلي/iu,
  occasions: /\b(?:special occasions?|wedding|party|parties|celebration|gala)\b|مناسبات|حفلات|فرح/iu,
};

const FAMILY_FIT = {
  floral: { seasons: ['spring', 'summer'], occasions: ['daily', 'occasions'] },
  soft_floral: { seasons: ['spring', 'autumn'], occasions: ['daily', 'work'] },
  floral_oriental: { seasons: ['autumn', 'winter'], occasions: ['evening', 'occasions'] },
  soft_oriental: { seasons: ['autumn', 'winter'], occasions: ['evening', 'daily'] },
  oriental: { seasons: ['winter', 'autumn'], occasions: ['evening', 'occasions'] },
  woody_oriental: { seasons: ['winter', 'autumn'], occasions: ['evening', 'occasions'] },
  woods: { seasons: ['autumn', 'winter'], occasions: ['work', 'daily'] },
  mossy: { seasons: ['autumn', 'spring'], occasions: ['work', 'evening'] },
  fougere: { seasons: ['spring', 'autumn'], occasions: ['work', 'daily'] },
  aromatic: { seasons: ['summer', 'spring'], occasions: ['daily', 'work'] },
  citrus: { seasons: ['summer', 'spring'], occasions: ['daily', 'work'] },
  fruity: { seasons: ['summer', 'spring'], occasions: ['daily', 'occasions'] },
};

function familiesIn(rawText) {
  let text = ` ${rawText} `;
  for (const [pattern, replacement] of EXCEPTIONS) text = text.replace(pattern, replacement);
  const has = {};
  for (const key of Object.keys(KEYWORDS)) has[key] = KEYWORDS[key].test(text);
  const found = new Set();
  if (has.floral) found.add('floral');
  if (has.floral && has.musk) found.add('soft_floral');
  if (has.floral && (has.sweet || has.oriental)) found.add('floral_oriental');
  if (has.sweet) found.add('soft_oriental');
  if (has.oriental) found.add('oriental');
  if (has.woods && has.oriental) found.add('woody_oriental');
  if (has.woods) found.add('woods');
  if (has.mossy) found.add('mossy');
  if (has.fougereWord || (has.lavender && (has.woods || has.tonka))) found.add('fougere');
  if (has.aromatic) found.add('aromatic');
  if (has.citrus) found.add('citrus');
  if (has.fruity) found.add('fruity');
  return found;
}

function pick(map, text) {
  return new Set(Object.keys(map).filter((key) => map[key].test(text)));
}

const PAGE_SIZE = 250;
const CARDS_STEP = 24;
const MAX_PAGES = 12;

function classify(raw, index) {
  const description = raw.d || '';
  const meta = `${raw.t} ${raw.ty || ''} ${raw.tg || ''}`;
  const everything = `${meta} ${description}`;
  // The short summary (title plus the opening of the description) sets the main character; any later note adds a touch.
  const primary = familiesIn(`${raw.t} ${raw.ty || ''} ${description.slice(0, 320)}`);
  const all = familiesIn(everything);
  for (const family of primary) all.add(family);
  // The title decides first, then type and tags, then the description, so a stray "him" in a story doesn't make a women's perfume unisex.
  let gender = null;
  for (const source of [raw.t, `${raw.ty || ''} ${raw.tg || ''}`, description]) {
    const genders = pick(GENDER, source);
    if (genders.has('unisex') || (genders.has('women') && genders.has('men'))) gender = 'unisex';
    else if (genders.has('women')) gender = 'women';
    else if (genders.has('men')) gender = 'men';
    if (gender) break;
  }
  // Descriptions rarely name a season or occasion, so the perfume's main families add the classic perfumery fit on top of any explicit mention.
  const seasons = pick(SEASONS, everything);
  const occasions = pick(OCCASIONS, everything);
  const basis = primary.size ? primary : all;
  for (const family of basis) {
    for (const season of FAMILY_FIT[family].seasons) seasons.add(season);
    for (const occasion of FAMILY_FIT[family].occasions) occasions.add(occasion);
  }
  // A perfume with no recognisable notes still belongs in the wheel: it fits any season or occasion it doesn't name, so the gender, season and occasion filters always reach it.
  const unsorted = all.size === 0;
  const anySeason = unsorted && seasons.size === 0;
  const anyOccasion = unsorted && occasions.size === 0;
  return { ...raw, index, primary, families: all, gender, seasons, occasions, unsorted, anySeason, anyOccasion };
}

class ScentWheel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.products = [];
    this.shown = CARDS_STEP;
    this.moreButton = this.querySelector('[data-more]');
    this.moreButton?.addEventListener('click', () => {
      this.shown += CARDS_STEP;
      this.render();
    });
    this.loaded = false;
    this.state = { gender: 'all', season: 'all', occasion: 'all', family: null };
    // Translations arrive HTML-escaped (e.g. &#39;), so decode them once before using textContent.
    const decoder = document.createElement('textarea');
    this.copy = JSON.parse(this.querySelector('[data-copy]').textContent, (key, value) => {
      if (typeof value !== 'string') return value;
      decoder.innerHTML = value;
      return decoder.value;
    });
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.segments = [...this.querySelectorAll('[data-family]')];
    this.filterButtons = [...this.querySelectorAll('[data-filter]')];
    this.ring = this.querySelector('[data-ring]');
    this.centerTitle = this.querySelector('[data-center-title]');
    this.centerSub = this.querySelector('[data-center-sub]');
    this.countLine = this.querySelector('[data-count]');
    this.results = this.querySelector('[data-results]');
    this.grid = this.querySelector('[data-grid]');
    this.cardTemplate = this.querySelector('[data-card-template]');

    this.addEventListener('command', (event) => this.onCommand(event), { signal });
    for (const segment of this.segments) {
      segment.addEventListener('click', () => this.selectFamily(segment.dataset.family), { signal });
      segment.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.selectFamily(segment.dataset.family);
      }, { signal });
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.load();
    }, { rootMargin: '800px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  async load() {
    try {
      // Every page of the collection: the feed (or product JSON fallback) is read 250 at a time until a short page.
      let useFallback = false;
      let data = await this.fetchFeed(1).catch((error) => {
        if (error.name === 'AbortError') throw error;
        return null;
      });
      // Previews that don't serve alternate templates fall back to the collection's product JSON.
      if (!Array.isArray(data)) {
        useFallback = true;
        data = await this.fetchFallback(1);
      }
      this.products = data.map(classify);
      this.loaded = true;
      this.render();
      let page = 1;
      let last = data;
      while (last.length >= PAGE_SIZE && page < MAX_PAGES) {
        page += 1;
        last = useFallback ? await this.fetchFallback(page) : await this.fetchFeed(page);
        if (!Array.isArray(last) || !last.length) break;
        const offset = this.products.length;
        this.products.push(...last.map((raw, index) => classify(raw, offset + index)));
        this.render();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.countLine.textContent = this.copy.error;
    }
  }

  pageUrl(base, page) {
    const url = new URL(base, window.location.origin);
    if (page > 1) url.searchParams.set('page', String(page));
    return url.toString();
  }

  async fetchFeed(page = 1) {
    const response = await fetch(this.pageUrl(this.dataset.endpoint, page), { signal: this.controller.signal });
    if (!response.ok) throw new Error(String(response.status));
    return JSON.parse(await response.text());
  }

  async fetchFallback(page = 1) {
    const response = await fetch(this.pageUrl(this.dataset.fallback, page), { signal: this.controller.signal });
    if (!response.ok) throw new Error(String(response.status));
    const { products } = await response.json();
    const money = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'EGP' });
    const root = (this.dataset.productsRoot || '/').replace(/\/$/, '');
    const text = document.createElement('div');
    return products.map((product) => {
      const variant = product.variants.find((item) => item.available) || product.variants[0] || {};
      const price = Number(variant.price || 0);
      const compare = Number(variant.compare_at_price || 0);
      text.innerHTML = product.body_html || '';
      const image = product.images?.[0];
      return {
        t: product.title,
        u: `${root}/products/${product.handle}`,
        i: image ? `${image.src}${image.src.includes('?') ? '&' : '?'}width=600&height=600&pad_color=ffffff` : null,
        a: image?.alt || product.title,
        p: money.format(price),
        c: compare > price ? money.format(compare) : null,
        av: product.variants.some((item) => item.available),
        ty: product.product_type,
        tg: (product.tags || []).join(' | '),
        d: text.textContent.replace(/\s+/g, ' ').trim().slice(0, 1400),
      };
    });
  }

  onCommand(event) {
    const source = event.source;
    if (event.command === '--filter') {
      if (source.disabled) return;
      this.state[source.dataset.filter] = source.dataset.value;
      this.render();
    } else if (event.command === '--see') {
      this.scrollToResults();
    } else if (event.command === '--reset') {
      this.state = { gender: 'all', season: 'all', occasion: 'all', family: null };
      this.render();
    }
  }

  selectFamily(family) {
    const segment = this.segments.find((item) => item.dataset.family === family);
    if (segment?.getAttribute('aria-disabled') === 'true') return;
    this.state.family = this.state.family === family ? null : family;
    this.render();
    if (this.state.family) requestAnimationFrame(() => this.scrollToResults());
  }

  scrollToResults() {
    if (this.results.hidden) return;
    this.results.scrollIntoView({ behavior: this.reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
  }

  matches(product, state, skip) {
    if (skip !== 'gender' && state.gender !== 'all') {
      const ok = product.gender === state.gender || (product.gender === 'unisex' && state.gender !== 'unisex');
      if (!ok) return false;
    }
    if (skip !== 'season' && state.season !== 'all' && !product.anySeason && !product.seasons.has(state.season)) return false;
    if (skip !== 'occasion' && state.occasion !== 'all' && !product.anyOccasion && !product.occasions.has(state.occasion)) return false;
    if (skip !== 'family' && state.family && !product.families.has(state.family)) return false;
    return true;
  }

  render() {
    const { state } = this;

    for (const button of this.filterButtons) {
      const group = button.dataset.filter;
      const value = button.dataset.value;
      button.setAttribute('aria-pressed', String(state[group] === value));
      if (!this.loaded || value === 'all') {
        button.disabled = false;
        continue;
      }
      const trial = { ...state, [group]: value };
      button.disabled = !this.products.some((product) => this.matches(product, trial));
    }

    let available = 0;
    for (const segment of this.segments) {
      const family = segment.dataset.family;
      const count = this.loaded ? this.products.filter((product) => product.families.has(family) && this.matches(product, state, 'family')).length : 1;
      const disabled = count === 0;
      if (!disabled) available += 1;
      if (disabled && state.family === family) state.family = null;
      segment.setAttribute('aria-disabled', String(disabled));
      segment.tabIndex = disabled ? -1 : 0;
    }
    for (const segment of this.segments) {
      const selected = segment.dataset.family === state.family;
      segment.setAttribute('aria-pressed', String(selected));
      if (selected) this.ring.setAttribute('d', segment.querySelector('path').getAttribute('d'));
    }
    if (!state.family) this.ring.setAttribute('d', '');

    const familyCopy = state.family ? this.copy.families[state.family] : null;
    this.centerTitle.textContent = familyCopy ? familyCopy.name : this.copy.centerTitle;
    this.centerSub.textContent = familyCopy ? familyCopy.alt : this.copy.centerSub;

    if (!this.loaded) return;
    this.countLine.textContent = this.copy.familiesCount.replace('__COUNT__', available);

    const visible = this.products.filter((product) => this.matches(product, state));
    this.renderPanel(familyCopy, visible.length);

    const active = Boolean(state.family) || state.gender !== 'all' || state.season !== 'all' || state.occasion !== 'all';
    this.results.hidden = !active;
    if (!active) return;
    this.renderGrid(visible);
  }

  renderPanel(familyCopy, matchCount) {
    const panel = this.querySelector('[data-panel]');
    panel.querySelector('[data-panel-empty]').hidden = Boolean(familyCopy);
    const body = panel.querySelector('[data-panel-body]');
    body.hidden = !familyCopy;
    const see = this.querySelector('[data-see]');
    see.hidden = !familyCopy || matchCount === 0;
    if (!familyCopy) return;

    const family = this.state.family;
    const color = this.segments.find((item) => item.dataset.family === family).querySelector('path').getAttribute('fill');
    body.querySelector('[data-panel-bar]').style.backgroundColor = color;
    body.querySelector('[data-panel-name]').textContent = familyCopy.name;
    body.querySelector('[data-panel-alt]').textContent = familyCopy.alt;
    body.querySelector('[data-panel-desc]').textContent = familyCopy.desc;
    this.fillChips(body.querySelector('[data-panel-notes]'), familyCopy.notes.split(',').map((note) => note.trim()), color);

    const inFamily = this.products.filter((product) => product.families.has(family));
    const optionLabel = (group, value) => this.querySelector(`[data-filter="${group}"][data-value="${value}"]`)?.textContent.trim() || value;
    const seasons = Object.keys(SEASONS).filter((key) => inFamily.some((product) => product.seasons.has(key))).map((key) => optionLabel('season', key));
    const occasions = Object.keys(OCCASIONS).filter((key) => inFamily.some((product) => product.occasions.has(key))).map((key) => optionLabel('occasion', key));
    body.querySelector('[data-panel-seasons-wrap]').hidden = seasons.length === 0;
    body.querySelector('[data-panel-occasions-wrap]').hidden = occasions.length === 0;
    this.fillChips(body.querySelector('[data-panel-seasons]'), seasons);
    this.fillChips(body.querySelector('[data-panel-occasions]'), occasions);

    const cheers = familyCopy.cheers.split('|');
    body.querySelector('[data-panel-cheer]').textContent = cheers[Math.floor(Math.random() * cheers.length)];
    see.textContent = this.copy.seeFamily.replace('__FAMILY__', familyCopy.name).replace('__COUNT__', matchCount);
  }

  fillChips(list, labels, color) {
    list.replaceChildren(...labels.map((label) => {
      const chip = document.createElement('li');
      chip.className = 'rounded-full border border-border px-3 py-1 text-xs font-medium';
      if (color) chip.style.backgroundColor = color;
      chip.textContent = label;
      return chip;
    }));
  }

  renderGrid(visible) {
    const family = this.state.family;
    const sorted = [...visible].sort((a, b) => {
      if (family) {
        const diff = Number(b.primary.has(family)) - Number(a.primary.has(family));
        if (diff) return diff;
      }
      const stock = Number(b.av) - Number(a.av);
      return stock || a.index - b.index;
    });

    this.querySelector('[data-results-status]').textContent = this.copy.resultsCount.replace('__COUNT__', sorted.length);
    this.querySelector('[data-empty]').hidden = sorted.length > 0;

    const filterKey = JSON.stringify(this.state);
    if (filterKey !== this.lastFilterKey) {
      this.lastFilterKey = filterKey;
      this.shown = CARDS_STEP;
    }
    if (this.moreButton) this.moreButton.hidden = sorted.length <= this.shown;
    const cards = sorted.slice(0, this.shown).map((product, position) => {
      const fragment = this.cardTemplate.content.cloneNode(true);
      const slot = (name) => fragment.querySelector(`[data-slot="${name}"]`);
      slot('link').href = product.u;
      const image = slot('img');
      if (product.i) {
        image.src = product.i;
        image.alt = product.a || product.t;
        if (position < 4) image.loading = 'eager';
      } else {
        image.remove();
      }
      slot('title').textContent = product.t;
      slot('price').textContent = product.p;
      if (product.c) {
        slot('price').classList.add('text-sale');
        slot('compare').hidden = false;
        slot('compare-value').textContent = product.c;
      }
      slot('soldout').hidden = product.av;

      const badgeFamily = family || [...product.primary][0] || null;
      const badge = slot('badge');
      if (badgeFamily) {
        const isPrimary = product.primary.has(badgeFamily);
        const name = this.copy.families[badgeFamily].name;
        const color = this.segments.find((item) => item.dataset.family === badgeFamily).querySelector('path').getAttribute('fill');
        badge.textContent = (isPrimary ? this.copy.badgePrimary : this.copy.badgeAccent).replace('__FAMILY__', name);
        if (isPrimary) badge.style.backgroundColor = color;
        else badge.style.borderColor = color;
      } else {
        badge.remove();
      }
      return fragment;
    });
    this.grid.replaceChildren(...cards);
  }
}

if (!customElements.get('s-scent-wheel')) customElements.define('s-scent-wheel', ScentWheel);
